import { useMemo } from 'react';

import { useExperimentFlagExposed } from '@zenbusiness/application-commons-experiments';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import useFlowConfig from '#client/hooks/flow/useFlowConfig';

function useFlowPageExperiment(pageComponentName, pageComponentProps) {
  const captureException = useCaptureException();
  const { currentExperiment: flowConfigExperimentKey, featureFlags = {} } = useFlowConfig();

  const currentPageExperiment = Object.entries(featureFlags).find(([, config]) =>
    config?.evaluateOnPages?.includes(pageComponentName)
  );

  const [experimentKey, experimentConfig] = currentPageExperiment || [];

  // Defensive check to ensure we don't evaluate the experiment if it's already active on the flow.
  const isFlowConfigExperimentActive = flowConfigExperimentKey === experimentKey;
  const skipEvaluation = isFlowConfigExperimentActive || !experimentKey || !experimentConfig;

  const experimentFlag = useExperimentFlagExposed(experimentKey, null, false, skipEvaluation);

  const experiment = {
    config: experimentConfig,
    key: experimentKey,
    ...experimentFlag
  };

  const variationKey = experiment.value;

  const props = useMemo(() => {
    // Return original props if we don't have the necessary data to apply variations
    if (!experimentConfig || !variationKey) {
      return pageComponentProps;
    }

    // Check if variations exist
    const { variations } = experimentConfig;
    if (!variations) {
      captureException(`No variations found in flow page experiment "${experimentKey}".`, {
        level: 'warning'
      });
      return pageComponentProps;
    }

    // Check if the specific variation exists
    const variation = variations[variationKey];
    if (!variation) {
      captureException(
        `Variation key "${variationKey}" not found in flow page experiment "${experimentKey}".`,
        { level: 'warning' }
      );
      return pageComponentProps;
    }

    // Check if pageComponentProps exists for this component in the variation
    const pageVariationProps = variation.pageConfigs?.[pageComponentName]?.pageComponentProps;
    if (!pageVariationProps) {
      return pageComponentProps;
    }

    // Merge the props
    return {
      ...pageComponentProps,
      ...pageVariationProps
    };
  }, [
    captureException,
    experimentConfig,
    experimentKey,
    pageComponentName,
    pageComponentProps,
    variationKey
  ]);

  return useMemoObject({
    experiment,
    props
  });
}

export default useFlowPageExperiment;
